import { useCallback, useRef, useState } from 'react'

// Undo and redo for a post, like the gift card designer: every change is a
// step, and changes to the same thing close together (typing a word) are one.
// set(next, { key, record: false }) changes without a step (things the studio
// works out for itself, like a photo's colour).
export const useHistory = (initial, { limit = 100, mergeMs = 450 } = {}) => {
  const [state, setState] = useState({ past: [], present: initial, future: [] })
  const last = useRef({ key: null, at: 0 })

  const set = useCallback((next, { key = null, record = true } = {}) => {
    const now = Date.now()
    const merge = !!key && last.current.key === key && now - last.current.at < mergeMs
    if (record) last.current = { key, at: now }
    setState((s) => {
      const value = typeof next === 'function' ? next(s.present) : next
      if (value === s.present) return s
      if (!record) return { ...s, present: value }
      return { past: merge ? s.past : [...s.past, s.present].slice(-limit), present: value, future: [] }
    })
  }, [limit, mergeMs])

  const undo = useCallback(() => {
    last.current = { key: null, at: 0 }
    setState((s) => (s.past.length
      ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1], future: [s.present, ...s.future] }
      : s))
  }, [])
  const redo = useCallback(() => {
    last.current = { key: null, at: 0 }
    setState((s) => (s.future.length
      ? { past: [...s.past, s.present], present: s.future[0], future: s.future.slice(1) }
      : s))
  }, [])

  return { value: state.present, set, undo, redo, canUndo: state.past.length > 0, canRedo: state.future.length > 0 }
}
